import { useEffect } from "react"
import useLocalStorage from "@kit/design/use-localstorage"
import { useCurrentUser } from "hooks/use-current-user"

// Written by the /ads/offers page on visit (NET-4590) and read here to decide
// whether the Sponsorships nav item shows its "unseen offers" dot. Exported so the
// reader and writer share one key and never drift. Value is ms-since-epoch, to
// match the `Date.now()` the page stores.
export const OFFERS_LAST_SEEN_AT_KEY = "ads:offers-last-seen-at"

/**
 * Whether the creator has sponsorship offers newer than the last time they
 * visited /ads/offers — the signal behind the vertical-nav dot.
 *
 * `latest_sponsorship_offer_at` is only present on window.app.user when the
 * feature flag is on and the account has an available offer, so an absent value
 * (flag off, or no offers) yields `false` with no async flag check and no
 * first-paint flicker. A creator who has never visited defaults to `0`, so any
 * available offer surfaces the dot.
 *
 * @returns {boolean}
 */
export function useHasUnseenSponsorshipOffers() {
  const { latest_sponsorship_offer_at: latestOfferAt } = useCurrentUser()
  const [lastSeenAt] = useLocalStorage(OFFERS_LAST_SEEN_AT_KEY, 0)

  const hasUnseen = Boolean(latestOfferAt) && latestOfferAt > lastSeenAt

  return hasUnseen
}

/**
 * Stamps the current time into the shared key on mount, marking the creator's
 * offers as seen. Call from the /ads/offers page so the nav dot clears once they
 * land there — the clearing is page-level, not per-offer, by design (NET-4590).
 *
 * The nav reads a separate `useLocalStorage` instance, so the cleared state
 * surfaces on the nav's next mount / page load rather than instantly — which
 * matches the "gone next time they look" acceptance criterion.
 */
export function useMarkSponsorshipOffersSeen() {
  const [, setLastSeenAt] = useLocalStorage(OFFERS_LAST_SEEN_AT_KEY, 0)

  useEffect(() => {
    setLastSeenAt(Date.now())
  }, [setLastSeenAt])
}
